/**
 * Vendored-library registry — the single source of truth for the heavy,
 * worker/WASM-backed libraries the platform self-hosts on `cdn.{env}.fias.io`
 * and lends to plugins that declare the `sandbox:vendored-libraries` permission.
 *
 * WHY this exists: a plugin runs in an opaque-origin iframe under a strict CSP
 * (`default-src 'none'`, `connect-src 'none'`) with its deps externalized to
 * esm.sh via an importmap — so it cannot bundle a Web Worker or self-host large
 * asset files (CMaps, WASM, fonts). For libraries that need those, the platform
 * must (a) serve a pinned build from its trusted CDN and (b) widen the plugin's
 * CSP to reach exactly that origin. This registry captures both, declaratively,
 * per library.
 *
 * ADDING A LIBRARY (e.g. an onnxruntime-web WASM runtime) is a single new entry
 * here plus uploading its asset set to the CDN — NO new permission, SDK hook,
 * CSP function, or build-service branch. A plugin opts in by (1) listing the
 * library in its manifest `dependencies` and (2) declaring the
 * `sandbox:vendored-libraries` permission; the build/preview/host paths then
 * read this registry generically.
 *
 * The registry is keyed by the library's import specifier (its npm package /
 * bare-module name) so it intersects the plugin's `dependencies` directly and
 * doubles as the importmap key the build pins to the self-hosted build.
 *
 * BOUNDARY (ADR-012, two-plane capability doctrine — `docs/capability-placement.md`):
 * this registry is the **Library plane's delivery spec** — self-hosted bytes +
 * CSP for code an arche **runs itself**. It never dispatches, rate-limits, or
 * bills. Anything **invoked** is an Entity — a client-side entity may *consume*
 * a vendored runtime (e.g. a future onnxruntime-web entry), but the invocation
 * lives in the entity catalog, not here.
 */

import { PANEL_KIT_ASSET_VERSION, PDFJS_ASSET_VERSION } from './catalog';

/**
 * Placeholder substituted with the normalized CDN base URL when a library's CSP
 * directives are resolved. Any other token (e.g. `'blob:'`) is passed through
 * verbatim.
 */
export const VENDORED_CDN_PLACEHOLDER = '<cdn>';

export interface VendoredLibrarySpec {
  /** Bare import specifier — the importmap key AND the `dependencies` key. */
  importSpecifier: string;
  /** Immutable CDN path segment for this library's asset set (e.g. `pdfjs`). */
  cdnPrefix: string;
  /** Pinned version; forms the immutable `<cdnPrefix>/<version>/` prefix. */
  version: string;
  /** File-key → path (relative to `<cdnPrefix>/<version>/`). Trailing `/` marks
   *  a directory asset (e.g. a CMap dir). Keys are echoed to the plugin. */
  files: Readonly<Record<string, string>>;
  /** Which `files` key is the ES module the importmap pins the specifier to. */
  mainModuleKey: string;
  /** CSP directives this library needs, with `VENDORED_CDN_PLACEHOLDER` where
   *  the CDN origin belongs. Merged into the plugin-wrapper CSP only when the
   *  plugin declares both the dependency and the capability permission. */
  csp: Readonly<Record<string, readonly string[]>>;
}

/**
 * The vendored libraries. Keyed by import specifier. pdf.js is the first entry;
 * a module worker's ENTIRE import graph is `worker-src`-governed (not
 * `script-src`), which is why the CDN origin appears in `worker-src` as well as
 * `script-src`, and `connect-src` covers the worker's CMap/font `fetch()`es.
 */
export const VENDORED_LIBRARIES: Readonly<Record<string, VendoredLibrarySpec>> = {
  'pdfjs-dist': {
    importSpecifier: 'pdfjs-dist',
    cdnPrefix: 'pdfjs',
    version: PDFJS_ASSET_VERSION,
    files: {
      mainModule: 'build/pdf.min.mjs',
      worker: 'build/pdf.worker.min.mjs',
      cMapUrl: 'cmaps/',
      standardFontDataUrl: 'standard_fonts/',
    },
    mainModuleKey: 'mainModule',
    csp: {
      'script-src': [VENDORED_CDN_PLACEHOLDER],
      'worker-src': ['blob:', VENDORED_CDN_PLACEHOLDER],
      'connect-src': [VENDORED_CDN_PLACEHOLDER],
    },
  },
  /**
   * The FIRST-PARTY panel renderer (see docs/panels.md). Unlike pdf.js this is
   * a single ESM file with React externalized — its bare `react` /
   * `react/jsx-runtime` imports resolve through the plugin iframe's importmap
   * like every plugin's own code — so its CSP need is `script-src` CDN ONLY:
   * no workers, no fetches, and its runtime style injection is covered by the
   * wrapper's baseline `style-src 'unsafe-inline'`. Uploaded by
   * `scripts/publish-panel-kit-to-cdn.ts` automatically in the deploy
   * workflows; the workspace package itself stays private (no npm channel).
   */
  '@fias/panel-kit': {
    importSpecifier: '@fias/panel-kit',
    cdnPrefix: 'panel-kit',
    version: PANEL_KIT_ASSET_VERSION,
    files: {
      mainModule: 'index.mjs',
    },
    mainModuleKey: 'mainModule',
    csp: {
      'script-src': [VENDORED_CDN_PLACEHOLDER],
    },
  },
};

/** Asset URLs for one vendored library (file-key → absolute URL), or `null`
 *  when the CDN base is unconfigured (caller must NOT vendor — no third-party
 *  fallback) or the specifier isn't a vendored library. */
export function vendoredLibraryAssetUrls(
  importSpecifier: string,
  cdnBaseUrl: string,
): Record<string, string> | null {
  const spec = VENDORED_LIBRARIES[importSpecifier];
  if (!spec) return null;
  const base = cdnBaseUrl.replace(/\/$/, '');
  if (!base) return null;
  const prefix = `${base}/${spec.cdnPrefix}/${spec.version}`;
  const urls: Record<string, string> = {};
  for (const [key, rel] of Object.entries(spec.files)) {
    urls[key] = `${prefix}/${rel}`;
  }
  return urls;
}

/** The importmap-pin URL (the main ES module) for a vendored library, or `null`. */
export function vendoredLibraryMainModuleUrl(
  importSpecifier: string,
  cdnBaseUrl: string,
): string | null {
  const spec = VENDORED_LIBRARIES[importSpecifier];
  if (!spec) return null;
  const urls = vendoredLibraryAssetUrls(importSpecifier, cdnBaseUrl);
  return urls ? urls[spec.mainModuleKey] : null;
}

/** CSP directives a vendored library needs, with the CDN origin substituted in,
 *  or `null` when the CDN base is unconfigured or the specifier isn't vendored. */
export function vendoredLibraryCspDirectives(
  importSpecifier: string,
  cdnBaseUrl: string,
): Record<string, string[]> | null {
  const spec = VENDORED_LIBRARIES[importSpecifier];
  if (!spec) return null;
  const base = cdnBaseUrl.replace(/\/$/, '');
  if (!base) return null;
  const out: Record<string, string[]> = {};
  for (const [directive, sources] of Object.entries(spec.csp)) {
    out[directive] = sources.map((s) => (s === VENDORED_CDN_PLACEHOLDER ? base : s));
  }
  return out;
}

/** A vendored library resolved for a plugin build: the importmap pin + the CSP
 *  directives it contributes. */
export interface ResolvedVendoredLibrary {
  importSpecifier: string;
  mainModuleUrl: string;
  cspDirectives: Record<string, string[]>;
}

/** A manifest dependency whose declared version isn't the one the platform serves. */
export interface VendoredVersionMismatch {
  importSpecifier: string;
  declared: string;
  expected: string;
}

/**
 * Check a manifest's `dependencies` against the versions the platform actually
 * serves, returning every mismatch (empty when all agree).
 *
 * **Why exact equality and not a semver range.** A vendored library is a
 * platform-pinned SINGLETON: there is exactly one build on the CDN at any time,
 * and the importmap pin is generated from the registry, never from the manifest.
 * A range would imply a choice the author does not have — and worse, would let
 * `"^0.1.0"` read as "I tested against 0.1.x" while the build silently serves
 * whatever the registry says today. Exact equality makes the manifest mean what
 * it appears to mean: *this is the build I was tested against.*
 *
 * **Why this exists at all.** `resolveVendoredLibraries` only ever reads the KEYS
 * of `dependencies`; the version string was decorative. That was invisible while
 * each library had exactly one version in its lifetime, and became a real hazard
 * the first time one was bumped — a plugin declaring the old version kept
 * building green while being served the new one. Callers turn these mismatches
 * into a hard build failure so an author is told, not silently upgraded.
 *
 * Non-vendored dependencies (ordinary npm packages resolved via esm.sh) are
 * ignored here — their versions are the author's business.
 */
/**
 * The registry flattened to `{ importSpecifier: requiredVersion }` — the shape
 * the build-time usage check wants. Derived rather than hand-written so a new
 * library or a version bump cannot land in one place and not the other.
 */
export const VENDORED_LIBRARY_VERSIONS: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(
    Object.entries(VENDORED_LIBRARIES).map(([specifier, spec]) => [specifier, spec.version]),
  ),
);

export function findVendoredVersionMismatches(
  dependencies: Record<string, string> | undefined,
): VendoredVersionMismatch[] {
  if (!dependencies) return [];
  const mismatches: VendoredVersionMismatch[] = [];
  for (const [importSpecifier, declared] of Object.entries(dependencies)) {
    const spec = VENDORED_LIBRARIES[importSpecifier];
    if (!spec) continue;
    if (declared !== spec.version) {
      mismatches.push({ importSpecifier, declared, expected: spec.version });
    }
  }
  return mismatches;
}

/** Human-readable build error for {@link findVendoredVersionMismatches}. */
export function describeVendoredVersionMismatches(
  mismatches: readonly VendoredVersionMismatch[],
): string {
  const lines = mismatches.map(
    (m) => `  "${m.importSpecifier}": "${m.declared}" → must be "${m.expected}"`,
  );
  return (
    'Manifest declares a vendored library version the platform does not serve. ' +
    'Vendored libraries are platform-pinned — update fias-plugin.json "dependencies" ' +
    `to the served version and rebuild:\n${lines.join('\n')}`
  );
}

/**
 * Resolve every vendored library a plugin uses: the intersection of its
 * manifest `dependencies` with the registry. Returns the importmap pin + CSP
 * contribution for each. Empty when the CDN is unconfigured, the plugin lists
 * no vendored dependency, or `dependencies` is absent. The CALLER still gates on
 * the `sandbox:vendored-libraries` permission before applying these.
 */
export function resolveVendoredLibraries(
  dependencies: Record<string, string> | undefined,
  cdnBaseUrl: string,
): ResolvedVendoredLibrary[] {
  if (!dependencies) return [];
  const resolved: ResolvedVendoredLibrary[] = [];
  for (const importSpecifier of Object.keys(dependencies)) {
    const mainModuleUrl = vendoredLibraryMainModuleUrl(importSpecifier, cdnBaseUrl);
    const cspDirectives = vendoredLibraryCspDirectives(importSpecifier, cdnBaseUrl);
    if (mainModuleUrl && cspDirectives) {
      resolved.push({ importSpecifier, mainModuleUrl, cspDirectives });
    }
  }
  return resolved;
}

/**
 * Asset URLs for every vendored library, keyed by import specifier — the shape
 * the host injects into the init payload (`useFiasVendoredAssets` reads it).
 * Empty when the CDN is unconfigured. Injecting the full set (rather than only
 * the plugin's declared libraries) is harmless: the URLs are public and the CSP
 * — widened only for declared libraries at build time — is what actually gates
 * loading.
 */
export function allVendoredLibraryAssets(
  cdnBaseUrl: string,
): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const importSpecifier of Object.keys(VENDORED_LIBRARIES)) {
    const urls = vendoredLibraryAssetUrls(importSpecifier, cdnBaseUrl);
    if (urls) out[importSpecifier] = urls;
  }
  return out;
}
